import axios from 'axios'
import { Fragment, useContext, useEffect, useState } from 'react'
import { render, createPortal } from 'react-dom'
import { ReactComponent as ChatIcon } from './assets/fab.svg'
import ChatEmphasizer from './components/ChatEmphasizer'
import LocalizationContext, {
  LocalizationProvider
} from './contexts/Localization/LocalizationContext'
import {
  EmphasizeMessage,
  EventType,
  installMobileViewportFixes,
  Language,
  ScriptActions,
  Sender,
  Voice
} from './globals'
import ChatUI, { ChatUIEvents } from './index'
import './script.scss'
import Markdown from './components/Markdown'

const UPDATED_SETTINGS = [
  'defaultLocale',
  'locale',
  'languages',
  'title',
  'primaryColor',
  'secondaryColor',
  'aivaTypingSpeed',
  'onMessage',
  'onEvent',
  'botName',
  'voices',

  'termsMessage',
  'termsFrequency',
  'recorderMode',
  'displayMode',
  'embedTargetId'
]

const KEY_MESSAGES_UNSEEN = 'inovia-messages-unseen'
const KEY_CHAT_IS_OPEN = 'inovia-chat-is-open'
const TEMP_SESSION_ID = 'TEMP_SESSION_ID'

export type Options = Partial<
  {
    // visual
    primaryColor: string
    secondaryColor: string
    title: string
    displayMode: 'chat' | 'phone'
    callUsLabel: string
    embedTargetId?: string
    botName?: string
    // localisation
    defaultLocale: string
    locale: string
    languages: Language[]
    voices?: Voice[]
    // header
    termsMessage?: string
    termsFrequency?: 'daily' | 'monthly'

    // behaviour
    recorderMode: 'disabled' | 'manual' | 'auto'
    aivaTypingSpeed: number
    isOpen: boolean
    visible: boolean
    freeText: boolean
    emphasizeMessages?: EmphasizeMessage[]
    emphasizeDelay: number
    emphasizeDisappear: number
    transcriptionDisabled?: boolean

    hideKnowledgeContent: boolean
    useGateway: boolean
    baseHost: string
  } & ChatUIEvents
>

function StartInoviaChatUI(options: Options = {}) {
  installMobileViewportFixes()

  const { baseHost = 'aiva.inoviaai.se', useGateway = true } = options
  const scriptElement = document.getElementById('inovia-script')
  const { publicKey } = scriptElement?.dataset || {}
  const { publicKeyName, publicKeySecret, parsingError } =
    parsePublicKey(publicKey)

  if (!publicKey) {
    console.error('Inovia Chat UI requires `data-public-key` field.')
    return
  }

  if (parsingError) {
    console.error('Passed `data-public-key` seem to be invalid.')
    return
  }

  let targetElement = document.getElementById('aiva-chat')
  if (!targetElement) {
    targetElement = document.createElement('div')
    targetElement.id = 'aiva-chat'
    document.body.appendChild(targetElement)
  }

  // let getSettingsHandler
  let updateSettingsHandler: (options: Options) => void
  let updateConversationProps: (value: unknown) => Promise<void>
  const sessionIsOpen = sessionStorage.getItem(KEY_CHAT_IS_OPEN)
  const isOpenInitialValue =
    typeof options.isOpen === 'boolean'
      ? options.isOpen
      : sessionIsOpen === 'true' || false
  const visibleInitialValue = options.visible || options.visible === undefined

  const phoneTranscript =
    options.displayMode === 'phone' && options.recorderMode === 'auto'

  const ChatWindow = () => {
    const defaultUnseen = Number(localStorage.getItem(KEY_MESSAGES_UNSEEN)) || 0
    const [isOpen, setIsOpenRaw] = useState(isOpenInitialValue)
    const [unseenMessages, setUnseenMessages] = useState(defaultUnseen)
    const [visible, setVisible] = useState(visibleInitialValue)
    const [, setCallstack] = useState<unknown[]>([])
    const [phoneActive, setPhoneActive] = useState(false)
    const [showTerms, setShowTerms] = useState(() => {
      /*  → If no termsMessage is configured we never show the overlay */
      if (!options.termsFrequency) return false

      /*  → Otherwise respect the chosen “daily / monthly” frequency */
      const last = Number(localStorage.getItem('INOVIA_TERMS_ACCEPTED') || 0)
      const ageMs =
        (options.termsFrequency === 'monthly' ? 30 : 1) * 24 * 60 * 60 * 1000
      return Date.now() - last > ageMs
    })

    const [sessionId, setSessionId] = useState<string | null>(null)
    const [settings, setSettings] = useState({
      title: options.title,
      primaryColor: options.primaryColor,
      secondaryColor: options.secondaryColor,
      defaultLocale: options.defaultLocale,
      locale: options.locale,
      languages: options.languages,
      aivaTypingSpeed: options.aivaTypingSpeed,
      onMessage: options.onMessage,
      onEvent: options.onEvent,
      onSwitchLocale: options.onSwitchLocale,
      botName: options.botName,
      voices: options.voices,
      activeLanguage:
        options.languages?.find((x) => x.locale == options.locale) ||
        options?.languages?.length
          ? options?.languages[0]
          : null,
      termsFrequency: options.termsFrequency,
      emphasizeDelay: options.emphasizeDelay,
      emphasizeDisappear: options.emphasizeDisappear,
      emphasizeMessages: options.emphasizeMessages,

      hideKnowledgeContent: options.hideKnowledgeContent,
      transcriptionDisabled: options.transcriptionDisabled
    })
    const [expanded, setExpanded] = useState(false)

    const { primaryColor, secondaryColor } = settings
    const visibilityClassNames = `${isOpen ? ' open' : ''}${
      !visible ? ' hidden' : ''
    }`
    const { locale } = useContext(LocalizationContext)

    function setIsOpen(value: boolean) {
      sessionStorage.setItem(KEY_CHAT_IS_OPEN, String(value))
      setIsOpenRaw(value)
    }

    const ensureUnlocked = () => {
      try {
        const a = new Audio()
        a.play().catch(() => {})
        a.pause()
        a.currentTime = 0
      } catch {
        /* ignore */
      }
    }

    updateConversationProps = async (value) => {
      if (sessionId && sessionId !== TEMP_SESSION_ID) {
        try {
          axios.patch(
            `${urlProps.conversationsUrl}/${sessionId}/properties`,
            value
          )
        } catch (err) {
          console.error(err)
        }
      } else {
        setCallstack((prev) => {
          return [...prev, value]
        })
      }
    }

    useEffect(() => {
      function keyUpHandler(e: KeyboardEvent) {
        // TODO: replace
        if (e.code === 'Escape') setIsOpen(false)
      }
      document.addEventListener('keyup', keyUpHandler)
      return () => document.removeEventListener('keyup', keyUpHandler)
    }, [])

    useEffect(() => {
      if (isOpen) setUnseenMessages(0)
    }, [isOpen])

    useEffect(() => {
      localStorage.setItem(KEY_MESSAGES_UNSEEN, unseenMessages.toString())
    }, [unseenMessages])

    useEffect(() => {
      updateColors({
        primaryColor,
        secondaryColor
      })
    }, [primaryColor, secondaryColor])

    useEffect(() => {
      if (sessionId) {
        setCallstack((prevCallstack) => {
          prevCallstack.forEach((value) => {
            updateConversationProps(value)
          })

          return []
        })
      }
    }, [sessionId])

    const targetLocale = options.defaultLocale || locale
    useEffect(() => {
      if (!options.emphasizeMessages) return

      const languageLocales = (
        options.languages
          ? options.languages.map((lang) => lang.locale)
          : [targetLocale]
      ).filter(Boolean)

      const isLocalizedMessages = options.emphasizeMessages
        .map((message) => Object.keys(message))
        .every((messageLocales) => {
          return languageLocales.every((item) => messageLocales.includes(item))
        })

      if (!isLocalizedMessages)
        console.error(`Localization is missing for ${languageLocales}.`)
    }, [targetLocale])

    const onMessage: Options['onMessage'] = (...args) => {
      const replies = args[0]
      const incomingMessages = replies.length
      if (!isOpen) setUnseenMessages(unseenMessages + incomingMessages)
      if (typeof settings.onMessage === 'function') settings.onMessage(...args)
    }

    // getSettingsHandler = (array) =>
    //   array.reduce((key, store) => ({ ...store, key: settings[key] }), {})

    axios.defaults.baseURL = `https://${baseHost}/${
      useGateway ? 'chat-ui' : ''
    }`

    const urlProps = {
      websocketUrl: `wss://${baseHost}/ws`,
      conversationsUrl:
        'customer-care-service/api/v2/customer-care/conversations',
      conversationStartUrl:
        'customer-care-service/api/v3/customer-care/conversations',
      conversationContinueUrl:
        'customer-care-service/api/v3/customer-care/conversations/continue',
      configUrl: useGateway
        ? 'management/api/mqtt/v1/config'
        : 'authentication-service/api/v1/mqtt/config',
      // transcriptionUrl: `https://caribou-singular-buffalo.ngrok-free.app/api/v1/transcribe`, //
      transcriptionUrl: `https://${baseHost}/chatasr-service/api/v1/transcribe`,
      baseHost
    }

    updateSettingsHandler = ({
      isOpen,
      visible,
      ...updatedSettings
    }: Options) => {
      if (typeof isOpen === 'boolean') {
        setIsOpen(isOpen)
      }
      if (typeof visible === 'boolean') {
        setVisible(visible)
      }
      Object.keys(updatedSettings).forEach((key) => {
        if (!UPDATED_SETTINGS.includes(key)) {
          delete updatedSettings[
            key as keyof Omit<Options, 'isOpen' | 'visible'>
          ]
        }
      })
      setSettings((settings) => ({
        ...settings,
        ...updatedSettings
      }))
    }

    const onEvent: Options['onEvent'] = (...args) => {
      if (typeof settings.onEvent === 'function') settings.onEvent(...args)

      const [type, data] = args
      if (type === EventType.NEW_SESSION) {
        setSessionId(data.id || null)
      }
    }

    const onSwitchLocale: Options['onSwitchLocale'] = (locale) => {
      if (typeof settings.onSwitchLocale === 'function') {
        settings.onSwitchLocale(locale)
      }
    }

    if (phoneTranscript && !phoneActive) {
      // console.log('Render Call us button')
      return (
        <button
          className="call-us-btn"
          onClick={() => {
            // console.log('Call us button')

            setPhoneActive(true) // ✅ show ChatUI
            setIsOpen(true) // 🟢 make the window show immediately
            ensureUnlocked()
            window.setTimeout(() => {
              window.document.dispatchEvent(new CustomEvent('AIVA_UNMUTE'))
            }, 0)
          }}
        >
          {options.callUsLabel ?? 'Call us'}
        </button>
      )
    }

    const channel = options.displayMode === 'phone' ? 'VOICE' : 'CHAT'
    const defaultLocale = settings?.defaultLocale
      ? settings?.defaultLocale
      : settings?.languages?.length
      ? settings?.languages[0].locale
      : 'sv-SE'

    const isRecorderEnabled = settings?.activeLanguage?.transcriptionUrl != null

    const restOfChatProps = {
      ...options,
      ...settings,
      channel,
      defaultLocale,
      isRecorderEnabled,
      ...urlProps
    }

    const termsOverlay =
      showTerms && settings?.activeLanguage?.termsMessage ? (
        <div className="TermsOverlay">
          <div className="TermsBox">
            <Markdown content={settings?.activeLanguage?.termsMessage} />

            <button
              onClick={() => {
                /* remember the acceptance date */
                localStorage.setItem(
                  'INOVIA_TERMS_ACCEPTED',
                  Date.now().toString()
                )
                setShowTerms(false) // ⬅ remove overlay → FAB clickable
              }}
            >
              OK
            </button>
          </div>
        </div>
      ) : null

    const ChatTree = (
      <Fragment>
        {termsOverlay} {/*  ⬅ add this line  */}
        {Array.isArray(options.emphasizeMessages) &&
          options.emphasizeMessages.length > 0 && (
            <ChatEmphasizer
              messages={settings.emphasizeMessages}
              display={settings.emphasizeDelay}
              disappear={settings.emphasizeDisappear}
              chatUiOpen={isOpen}
              locale={settings.defaultLocale || locale}
            />
          )}
        <button
          className={`chat-btn${visibilityClassNames}`}
          onClick={() => setIsOpen(!isOpen)}
          title={isOpen ? 'Hide chat' : 'Open chat'}
        >
          <span className="chat-btn-icons">
            <div className="badge">{unseenMessages || null}</div>
            <ChatIcon />
          </span>
        </button>
        <div
          className={`ChatContainer${visibilityClassNames} ${
            expanded ? 'expanded' : ''
          }`}
        >
          <div className="ChatOverlay">
            <ChatUI
              {...restOfChatProps}
              publicKeyName={publicKeyName}
              publicKeySecret={publicKeySecret}
              senderTypes={[Sender.CUSTOMER]}
              onMinimizeBtnClick={() => {
                // console.log(`Minimize button... ${options.displayMode}`)
                if (options.displayMode === 'phone') {
                  /* go back to CTA button view */

                  setPhoneActive(false)
                  setIsOpen(false)
                } else {
                  setIsOpen(false)
                }
              }}
              onMessage={onMessage}
              onEvent={onEvent}
              onSwitchLocale={onSwitchLocale}
              isChatWindowVisible={isOpen}
              phoneTranscript={phoneTranscript}
              autoStartRecorder={phoneTranscript}
              isExpanded={expanded}
              onToggleExpand={() => setExpanded((e) => !e)}
              languages={settings.languages}
              transcriptionDisabled={settings.transcriptionDisabled}
            />
          </div>
        </div>
      </Fragment>
    )

    if (phoneTranscript) {
      const host = document.getElementById(
        options.embedTargetId || 'call-transcription'
      )
      if (host) {
        /* Make sure we don't try to mount twice */
        return createPortal(ChatTree, host)
      }
    }

    /* ----------  Classic overlay fallback  ---------- */
    return ChatTree
  }

  render(
    <LocalizationProvider>
      <ChatWindow />
    </LocalizationProvider>,
    targetElement
  )

  return {
    // getSettingsHandler,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    updateSettingsHandler,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    updateConversationProps
  }
}

const COLOR_VAR_NAMES = {
  primaryColor: '--primary-color',
  secondaryColor: '--secondary-color'
}

function convertToRGBAValues(string: string) {
  if (string.startsWith('rgb'))
    return string.match(/[+-]?(\d*\.)?\d+/g)?.slice(0, 4)
  if (string.startsWith('#')) {
    const l = string.length < 6 ? 1 : 2
    return new RegExp(`^#?${`([a-f\\d]{${l}})`.repeat(4)}?`, 'i')
      .exec(string)
      ?.slice(1, 5)
      .map(
        (x = 'f'.repeat(l), i) =>
          parseInt(x.repeat(2 / l), 16) / (i === 3 ? 255 : 1)
      )
  }
  return null
}

function updateRootColor(propertyName: string, propertyValue: string) {
  const root = document.documentElement
  const currentValue = root.style.getPropertyValue(propertyName)
  const rgbaValues = convertToRGBAValues(propertyValue)
  const [base, values] =
    rgbaValues !== null
      ? [`rgba(${rgbaValues?.join()})`, rgbaValues?.slice(0, 3).join()]
      : [propertyValue, '153, 153, 153']
  if (currentValue !== base) {
    root.style.setProperty(propertyName, base)
    root.style.setProperty(`${propertyName}-values`, values || '')
  }
}

function updateColors(colorsObject: Partial<typeof COLOR_VAR_NAMES> = {}) {
  Object.entries(colorsObject).forEach(([color, colorCode]) => {
    if (colorCode)
      updateRootColor(
        COLOR_VAR_NAMES[color as keyof typeof COLOR_VAR_NAMES],
        colorCode
      )
  })
}

function parsePublicKey(publicKey = '') {
  try {
    // const [publicKeyName, publicKeySecret] = Buffer.from('Vm9pY2UgQ2hhdC0wNzJiYjNmOmQ5ZWYwMjg0LTc4MzctNDlkZC04MTgyLWRkNDMyNWIxMzNlZQ==', 'base64')

    const [publicKeyName, publicKeySecret] = Buffer.from(publicKey, 'base64')
      .toString()
      .split(':')
    return {
      parsingError: false,
      publicKeyName,
      publicKeySecret
    }
  } catch (err) {
    return {
      parsingError: true,
      publicKeyName: '',
      publicKeySecret: ''
    }
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
const { q = [] } = window.inovia || { q: [] }

if (!window.inoviaLoaded) {
  window.inoviaLoaded = true
  const options = q.reduce(
    (
      store: Record<string, string>,
      [key, values]: [string, Record<string, string>]
    ) => (key === ScriptActions.SET ? { ...store, ...values } : store),
    {} as Record<string, string>
  )
  const {
    // getSettingsHandler,
    updateSettingsHandler,
    updateConversationProps
  } = StartInoviaChatUI(options) || {}

  window.inovia = (action, value) => {
    if (
      // !getSettingsHandler ||
      !updateSettingsHandler ||
      !updateConversationProps
    ) {
      return console.error(
        // eslint-disable-next-line max-len
        'There was an error while staring Inovia Chat UI, cannot dispatch any action.'
      )
    }
    switch (action) {
      // case ScriptActions.GET:
      //   return getSettingsHandler(value)
      case ScriptActions.SET:
        return updateSettingsHandler(value)
      case ScriptActions.UPDATE:
        return updateConversationProps(value)
      default:
        console.error(`None or unsupported action (${action}) specified.`)
    }
  }
  window.inovia.prototype.q = q
}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
for (const args of q) window.inovia(...args)
